import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { cariTypeLabel } from './cari-type-display';
import { usePermission } from '@/components/guards/permission-guard';

export type CariType = string;
interface NewCariDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (type: CariType) => void;
}

export function NewCariDialog({ open, onOpenChange, onSelect }: NewCariDialogProps) {
  const { t, i18n } = useTranslation('travelCari');
  const { t: common } = useTranslation('common');
  const canCreateCustomer = usePermission('CUSTOMERS', 'CREATE', 'TRAVEL');
  const canCreatePartner = usePermission('PARTNERS', 'CREATE', 'TRAVEL');
  const [selected, setSelected] = useState('');
  const { data, isLoading, error, refetch } = trpc.travelCariType.list.useQuery(undefined, {
    enabled: open,
  });
  useEffect(() => {
    if (open) setSelected('');
  }, [open]);
  const options = (data ?? [])
    .filter((type) => (type.source === 'partner' ? canCreatePartner : canCreateCustomer))
    .map((type) => ({
      value: type.code,
      label: `${type.code} · ${cariTypeLabel(type, i18n.language)}`,
      description: t(type.source === 'customer' ? 'cari.customerGroup' : 'cari.partnerGroup'),
      keywords: [type.code, type.name, type.nameEn ?? ''],
    }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('cari.selectType')}</DialogTitle>
          <DialogDescription>{t('cari.selectTypeDesc')}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm" role="status">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('account.loading')}
          </p>
        ) : error ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>{t('account.loadFailed')}</p>
            <Button
              variant="outline"
              onClick={() => {
                void refetch();
              }}
            >
              {t('account.retry')}
            </Button>
          </div>
        ) : options.length ? (
          <div className="space-y-2">
            <Label>{t('directory.type')}</Label>
            <Combobox
              ariaLabel={t('directory.type')}
              options={options}
              value={selected}
              onValueChange={setSelected}
              placeholder={t('cari.selectType')}
              searchPlaceholder={t('cari.typeSearch')}
              emptyText={t('directory.empty')}
            />
          </div>
        ) : (
          <p role="status" className="text-muted-foreground text-sm">
            {t('cari.noAvailableTypes')}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {common('cancel')}
          </Button>
          <Button
            disabled={!!error || isLoading || !options.some((option) => option.value === selected)}
            onClick={() => {
              onSelect(selected);
              onOpenChange(false);
            }}
          >
            {t('cari.continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
